import { queryOptions } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { Insert, Tables } from "@/types/database";
import { invokeEdgeFunction } from "./_edgeFunctions";
import { qk } from "./_queryKeys";
import { assertData, throwIfError } from "./_supabaseErrors";

export type EmailDraft = Tables<"email_drafts">;
export type EmailDraftInsert = Insert<"email_drafts">;
export type InboundEmail = Tables<"emails">;

export async function listPendingEmailDrafts(): Promise<EmailDraft[]> {
  const { data, error } = await supabase
    .from("email_drafts")
    .select("*")
    .eq("status", "pending_ok")
    .order("created_at", { ascending: false });
  throwIfError(error);
  return data ?? [];
}

export async function listEmailDrafts(limit = 30): Promise<EmailDraft[]> {
  const { data, error } = await supabase
    .from("email_drafts")
    .select("*")
    .order("created_at", { ascending: false })
    .limit(limit);
  throwIfError(error);
  return data ?? [];
}

export async function createEmailDraft(values: EmailDraftInsert): Promise<EmailDraft> {
  const { data, error } = await supabase.from("email_drafts").insert(values).select("*").single();
  throwIfError(error);
  return assertData(data);
}

export async function updateEmailDraftText(
  id: string,
  values: { subject?: string; body?: string; to_address?: string }
): Promise<EmailDraft> {
  const { data, error } = await supabase
    .from("email_drafts")
    .update(values)
    .eq("id", id)
    .select("*")
    .single();
  throwIfError(error);
  return assertData(data, "A draft nem található.");
}

export async function cancelEmailDraft(id: string): Promise<void> {
  const { error } = await supabase
    .from("email_drafts")
    .update({ status: "cancelled" })
    .eq("id", id)
    .eq("status", "pending_ok");
  throwIfError(error);
}

/**
 * Küldés: kizárólag az Edge Function küld ténylegesen (ott van a Gmail token és
 * a tiltólista-ellenőrzés). A kliens csak jóváhagyást indít.
 */
export async function sendEmailDraft(id: string): Promise<void> {
  const result = await invokeEdgeFunction<{ ok?: boolean; error?: string }>(
    "gmail-send",
    { draft_id: id },
    "A küldés nem sikerült."
  );
  if (result.ok !== true) throw new Error(result.error ?? "A küldés nem sikerült.");
}

export async function listRecentEmails(limit = 20): Promise<InboundEmail[]> {
  const { data, error } = await supabase
    .from("emails")
    .select("*")
    .order("received_at", { ascending: false })
    .limit(limit);
  throwIfError(error);
  return data ?? [];
}

export const pendingEmailDraftsQueryOptions = () =>
  queryOptions({
    queryKey: [...qk.emailDrafts, "pending"] as const,
    queryFn: listPendingEmailDrafts
  });

export const emailDraftsQueryOptions = (limit = 30) =>
  queryOptions({
    queryKey: [...qk.emailDrafts, limit] as const,
    queryFn: () => listEmailDrafts(limit)
  });

export const recentEmailsQueryOptions = (limit = 20) =>
  queryOptions({
    queryKey: [...qk.emails, limit] as const,
    queryFn: () => listRecentEmails(limit)
  });
